import { ArrowUpRightSVG } from '@/assets/svgs';
import { EmailSVG, PhoneSVG, UserSVG } from '@/assets/svgs/contact-form';
import BaseButton from '@/components/BaseButton';
import { TextDefault } from '@/components/Texts';
import { useSendContactMutation } from '@/queries/apis.query';
import type { SendContactBody } from '@/services/apis.service';
import { colors } from '@/styles/theme/colors';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Textarea,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'next-i18next';
import { FieldError, useForm } from 'react-hook-form';
import * as yup from 'yup';

type FieldDetails = {
  [key: string]: {
    label: string;
    icon?: React.ElementType;
    isTextArea?: boolean;
  };
};

type ExtendedContactBody = SendContactBody & {
  phone: string;
};

const fieldDetails: FieldDetails = {
  subject: { label: 'contact-us.get-in-touch.subject', icon: UserSVG },
  name: { label: 'contact-us.get-in-touch.name', icon: UserSVG },
  email: { label: 'contact-us.get-in-touch.email', icon: EmailSVG },
  phone: { label: 'contact-us.get-in-touch.phone', icon: PhoneSVG },
  message: { label: 'contact-us.get-in-touch.message', isTextArea: true },
};

const contactSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  subject: yup.string().required('Subject is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  message: yup.string().required('Message is required'),
  phone: yup.string().required('Phone is required'),
}) as any;

const ContactForm = () => {
  const { t } = useTranslation('main');
  const sendContactMutation = useSendContactMutation();
  const formMethods = useForm<ExtendedContactBody>({
    defaultValues: { subject: '', name: '', email: '', message: '', phone: '' },
    resolver: yupResolver(contactSchema),
  });

  const errors = formMethods.formState.errors;

  const handleSubmit = formMethods.handleSubmit((body) => {
    sendContactMutation.mutate(
      {
        name: body.name,
        email: body.email,
        subject: body.subject,
        message: body.message + ' ' + body.phone,
      },
      {
        onSuccess: () => {
          formMethods.reset();
        },
      },
    );
  });

  const renderFormControl = (key: keyof ExtendedContactBody) => {
    const { label, icon, isTextArea } = fieldDetails[key];
    const Component = isTextArea ? Textarea : Input;

    return (
      <FormControl key={key} isRequired isInvalid={!!errors[key]}>
        <FormLabel
          requiredIndicator={
            <span style={{ color: colors.diment.lightBlue }}>*</span>
          }
        >
          {t(label)}
        </FormLabel>
        <InputGroup>
          <Component
            size='lg'
            variant='filled'
            bg={colors.diment.lighterBlueTransparent}
            {...formMethods.register(key)}
            rows={isTextArea ? 6 : undefined}
          />
          {!isTextArea && (
            <InputRightElement
              height='100%'
              display='flex'
              alignItems='center'
              justifyContent='center'
            >
              <Icon as={icon} boxSize={6} />
            </InputRightElement>
          )}
        </InputGroup>
        <FormErrorMessage>
          {(errors[key] as FieldError)?.message}
        </FormErrorMessage>
      </FormControl>
    );
  };

  return (
    <Box as='form' onSubmit={handleSubmit} w='full' maxW='2xl'>
      <Stack spacing={4} px={{ base: 4, lg: 0 }} color={colors.diment.grey}>
        <Flex direction={{ base: 'column', sm: 'row' }} gap={4}>
          {renderFormControl('name')}
          {renderFormControl('subject')}
        </Flex>
        <Flex direction={{ base: 'column', sm: 'row' }} gap={4}>
          {renderFormControl('email')}
          {renderFormControl('phone')}
        </Flex>
        {renderFormControl('message')}
        <BaseButton
          rounded={'none'}
          bg={colors.diment.darkBlue}
          isLoading={sendContactMutation.isLoading}
          type='submit'
          maxW={150}
        >
          <Flex align='center' justify='center' gap='2' fontWeight={500}>
            <TextDefault fontWeight={400}>
              {t('contact-us.get-in-touch.submit')}
            </TextDefault>
            <ArrowUpRightSVG />
          </Flex>
        </BaseButton>
      </Stack>
    </Box>
  );
};

export default ContactForm;
